<template>
  <div class="jira-issue-reporter">
    <transition name="fade" mode="out-in">
      <div v-if="displayedByUrl" class="dark-overlay v-centerer">
        <div class="dark-overlay-content large">
          <CloseButton @click.native="hide" />
          <div class="form">
            <h2 class="basic-sub-title">
              {{ $t('message.JIRA_TRIGGER') }}
            </h2>
            <div class="legend">
              {{ $t('message.JIRA_WHAT_TYPE') }}
            </div>
            <RadioGroup v-model="selectedCategory" :items="types" />
            <EditableLabel
              v-model="description"
              class="mt10"
              :legend="$t('message.DESCRIPTION')"
              use-textarea
            />
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div class="field-hint centered" v-html="$t('message.JIRA_DESCRITION_HINT')" />
            <div class="images" @click="editPictures">
              <span class="legend">{{ this.$t('message.PICTURES') }}</span>
              <span class="field-hint ml10">{{ this.$t('message.MAXIMUM_SHORT_N', { n: maxPictures }) }}</span>
              <button class="add-button mt3em" style="padding-left: 0">
                <Icon>{{ addCircleMedium }}</Icon>
                <span>{{ $t('message.ADD_FILES') }}</span>
              </button>
            </div>
            <EditableLabel
              v-model="name"
              class="mt10 name"
              :legend="$t('message.YOUR_NAME')"
              use-textarea
            >
              <span slot="legend" class="field-hint ml10">{{ $t('message.OPTIONAL') }}</span>
            </EditableLabel>
            <label for="phone" class="mb3em block mt10em">
              <span class="legend">{{ $t('message.PHONE_NUMBER') }}</span>
              <span class="field-hint ml10em">{{ $t('message.OPTIONAL') }}</span>
            </label>
            <input
              v-model="phone"
              class="bordered-field bb f-w"
              type="tel"
              name="phone"
            >
            <label for="email" class="mb3em block mt10em">
              <span class="legend">{{ $t('message.EMAIL_ADDRESS') }}</span>
              <span class="field-hint ml10em">{{ $t('message.OPTIONAL') }}</span>
            </label>
            <input
              v-model="email"
              class="bordered-field bb f-w"
              type="email"
              name="email"
            >
            <button class="action-btn save-btn" @click="send">
              {{ $t('message.SEND') }}
            </button>
          </div>
        </div>
        <PicturesEditor ref="picturesEditor" @editionDone="picturesEditionDone" />
      </div>
    </transition>
  </div>
</template>

<script>
/**
 * Component featuring a way to report a bug or suggest a feature to Shizen's Jira platform
 * Also allows the user to access the issues reported by this company.
 *
 * No props available beause this is a global component but features two query params to use :
 *
 * @param {Boolean} jiraIssuesReporter - Set as a string === 'true' to display the reporting modal
 * @param {Number} jiraIssuesCategory - 0 to report a bug, 1 to report a new feature suggestion
 */

import $ from 'jquery';
import toastr from 'toastr';
import { isEqual } from 'lodash';
// Components
import CloseButton from '@/components/close-button.vue';
import EditableLabel from '@/components/editable-label.vue';
import Icon from '@/components/icon.vue';
import PicturesEditor from '@/components/pictures-editor.vue';
import RadioGroup from '@/components/radio-group.vue';
// State
import { FETCH_JIRA_ISSUES } from '@/store/modules/jira-issues/action-types';
// Utils
import { reduceMultipleImages } from '@/utils/main';
import { addCircleMedium } from '@/assets/icons';

export default {

  name: 'JiraIssueReporter',

  components: {
    CloseButton,
    EditableLabel,
    Icon,
    PicturesEditor,
    RadioGroup,
  },

  data() {
    return {
      addCircleMedium,
      maxPictures: 3,
      description: '',
      name: '',
      phone: '',
      email: '',
      pictures: [],
      types: [
        { id: 0, label: this.$t('message.JIRA_TYPE_BUG') },
        { id: 1, label: this.$t('message.JIRA_TYPE_IMPROVE') },
      ],
      sending: false,
    };
  },

  computed: {
    /** Boolean: Whether the modal should be displayed according to query params */
    displayedByUrl() {
      return this.$route.query.jiraIssuesReporter === 'true';
    },
    /** Number: Indexof the selected category */
    selectedCategory: {
      get() {
        return Number(this.$route.query.jiraIssuesCategory) === 1 ? 1 : 0;
      },
      set(category) {
        const query = Object.assign({}, this.$route.query);
        query.jiraIssuesCategory = category;
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
  },

  watch: {
    displayedByUrl() {
      if (this.displayedByUrl) {
        if (this.currentUser) {
          this.name = `${this.currentUser.firstName} ${this.currentUser.lastName}`;
          this.email = this.currentUser.email;
        }
        $(document).on(this.escKeyupEventName, this.hide);
      }
    },
  },

  beforeDestroy() {
    $(document).off(this.escKeyupEventName);
  },

  methods: {
    /** Hide the overlay */
    hide() {
      $(document).off(this.escKeyupEventName);
      this.pictures = [];
      this.description = '';
      this.name = '';
      this.phone = '';
      this.email = '';
      this.sending = false;
      const query = Object.assign({}, this.$route.query);
      query.jiraIssuesReporter = false;
      this.$router.replace({ query });
    },
    /** Report the issue */
    send() {
      if (this.sending) {
        return;
      }
      if (!this.description) {
        toastr.error(this.$t('message.JIRA_ISSUE_CREATION_ERROR_DESCRIPTION'));
        return;
      }
      this.sending = true;
      const params = {
        description: this.description,
        name: this.name,
        phone: this.phone,
        email: this.email,
        type: this.selectedCategory,
        page: this.$route.fullPath,
      };
      reduceMultipleImages(this.pictures, (resized) => {
        params.pictures = resized;
        this.$http.post('/server/jiraIssues/create.php', params).then((response) => {
          if (response.data === 1) {
            toastr.success(this.$t('message.JIRA_ISSUE_REPORTED'));
            this.hide(true);
            // Check if the user is on the issues listing to refresh the list
            if (this.$route.path === '/shizen-issues') {
              this.$store.dispatch(`jiraIssues/${FETCH_JIRA_ISSUES}`);
            }
          } else {
            toastr.error(this.$t('message.CONNECTION_ERROR'));
          }
          this.sending = false;
        });
      });
    },
    /** Pictures edition */
    editPictures() {
      this.$refs.picturesEditor.pictures = this.pictures;
      this.$refs.picturesEditor.show();
    },
    picturesEditionDone(pictures) {
      this.pictures = pictures.slice(0, this.maxPictures);
    },
  },
};
</script>

<style lang="scss">
@import '@/assets/scss/vars.scss';

.jira-issue-reporter {

  .dark-overlay {
    text-align: initial;
  }

  .dark-overlay-content {
    padding: 0px;
  }

  .form {
    padding: 0px 30px;
    overflow: auto;
    height: 100%;

    .images {
      cursor: pointer;
    }
    .name {
      .textarea {
        max-height: calc(1.5em + 8px);
        white-space: nowrap;
      }
    }
    .field-hint {
      text-align: center;
      font-size: 0.85em;
      font-style: italic;

      &.centered {
        text-align: center;
      }
    }
  }
}
</style>
